/**
 * Redaction for the Pinia snapshot Sentry attaches to every error event.
 *
 * The snapshot is sent twice: as the `state` context and as a `pinia_state.json`
 * attachment. Sentry's context normalization truncates nested values, but the
 * attachment is a full JSON dump, so anything left in the state ends up on the
 * error tracking backend verbatim. The SDK's `dataCollection` settings do not
 * cover this, and neither does Sentry's server-side scrubbing, which only knows
 * a handful of well-known field names.
 *
 * What is removed:
 *   - bearer tokens and credentials anywhere in the tree, including the session
 *     JWT (`jwt.state`) and the webmail JWT (`mailAuth.token`), which are valid
 *     for as long as they have not expired
 *   - contact details (email, phone, street, postcode), which for domain
 *     handles, delegations and sub-accounts belong to other people rather than
 *     to the reporting user
 *   - everything identifying a person beyond ids, for the collections that
 *     describe someone other than the reporting user
 *
 * What is kept: ids, types, states, flags, counts and domain names, so an
 * event still says what the user was looking at when it broke.
 */

const FILTERED = '[Filtered]'
const MAX_DEPTH = 10

/** Sent instead of the real payload when redaction itself fails. */
const REDACTION_FAILED = { redaction_error: true }

/**
 * Keys whose value is a credential regardless of where they appear. Separator
 * variants are matched because the same value is spelled differently across the
 * codebase: the domain transfer EPP code is `auth_code` on the domain detail
 * but `authcode` in a cart item's meta data.
 */
const SECRET_KEY_PATTERN =
  /(token|secret|password|passwd|auth[-_]?code|api[-_]?key|credential|kubeconfig|private[-_]?key|bearer|jwt|otp)/i

/**
 * Keys holding personal contact details. Matched on whole word parts so that
 * compound keys are covered too (`invite_email`, `delegator_email`), which an
 * exact-match list is not: `delegated_by` and `granted_by` are email addresses
 * despite not saying so (src/types/rbac.ts).
 */
const CONTACT_KEY_PATTERN =
  /(^|_)(emails?|phones?|mobile|fax|street|postcode|zip|delegated_by|granted_by|invited_by)(_|$)/i

/**
 * Keys the contact pattern would otherwise swallow: verification flags and
 * their timestamps, which are booleans and dates rather than contact data and
 * are the part of a handle worth keeping for triage.
 *
 * Note what is deliberately absent from the pattern above: a bare `mail`. Every
 * `mail_*` key in the type layer is a count, a byte total or a list of domain
 * labels (`mail_accounts`, `mail_bytes_used`, `mail_subdomains`); the only key
 * holding an address is spelled `email`. Matching `mail` would redact a hosting
 * site's usage figures and say nothing useful in exchange.
 */
const CONTACT_KEY_EXEMPTIONS = /^(email|phone)_verified(_at)?$/i

/**
 * Collections describing someone other than the reporting user, reduced to the
 * listed keys. An allow-list is used rather than more key patterns because the
 * identifying fields here are named too generically to match (`name`, `org`,
 * `city`, `label`) and would take unrelated stores with them.
 *
 * Applies to a value under one of these keys whether it is a single record or
 * an array of them. Kept values are still walked, so a domain list nested under
 * a sub-account is redacted normally.
 */
const PERSON_COLLECTION_KEYS: Record<string, readonly string[]> = {
  // Domain handles carry the full postal address of a contact who is
  // frequently not the account owner. `*_c` are the handle ids referenced from
  // a domain's own detail record, which is the same key holding plain numbers.
  // `handle` (singular) is the prop name components receive one under.
  handle: [
    'id',
    'external_id',
    'type',
    'country',
    'account_data',
    'needs_revalidation',
    'email_verified',
    'phone_verified'
  ],
  handles: [
    'id',
    'external_id',
    'type',
    'country',
    'account_data',
    'needs_revalidation',
    'email_verified',
    'phone_verified',
    'admin_c',
    'owner_c',
    'tech_c',
    'billing_c'
  ],
  child_account: ['id'],
  parent_account: ['id'],
  sub_accounts: ['account_id', 'domains']
}

/**
 * Fields kept on a record recognized as a person by its shape rather than by
 * the key it hangs off.
 */
const PERSON_SHAPE_SAFE_KEYS = [
  'id',
  'external_id',
  'account_id',
  'type',
  'country',
  'account_data',
  'needs_revalidation',
  // Not handle fields, but the shape rule also catches mail identities, send
  // limit rows and the account record, and without these an event from those
  // screens says nothing at all about what the user was looking at.
  'status',
  'request_status',
  'active',
  'verified',
  'is_primary',
  'role',
  'account_type'
] as const

/**
 * A contact record can arrive under any name at all: components take handle
 * arrays as `verifiedHandles`, `handlesPastDeadline` and similar, and those
 * props are attached to Sentry events. Matching the shape rather than the key
 * is what makes the rule hold for names nobody enumerated.
 */
const NAME_KEYS = ['name', 'org', 'surname', 'first_name', 'last_name', 'company']

/**
 * Contact keys that make a record a person, as opposed to CONTACT_KEY_PATTERN,
 * which decides whether a single field is contact data. The two are deliberately
 * not the same list, because a false positive costs different amounts: in the
 * field filter it loses one value, here it reduces an entire record to a couple
 * of ids.
 *
 * So this half takes unqualified keys only. A hosting site reporting
 * `mail_bytes_used` and an admin row carrying `account_email` are contact-shaped
 * names on records that describe a site and a domain, not a person, and reusing
 * the field pattern here classified both as people.
 */
const CONTACT_SHAPE_KEYS = ['email', 'phone', 'mobile', 'fax', 'street', 'postcode', 'zip']

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * True for a record that identifies a person: it names someone and carries a
 * way to reach them. Both halves are required so that domains, products and
 * sites, which have a `name` and nothing else personal, are left alone.
 *
 * A record that merely references someone's address, rather than being that
 * person, is not one: the field filter still redacts the address itself.
 */
function looksLikePerson(value: UnknownRecord): boolean {
  return (
    NAME_KEYS.some((key) => Object.hasOwn(value, key)) &&
    CONTACT_SHAPE_KEYS.some((key) => Object.hasOwn(value, key))
  )
}

/**
 * Reduces a person record, or an array of them, to `allowedKeys`. Applies the
 * same depth and cycle guards as the normal walk, so that an array nested
 * inside a summarized collection cannot escape them.
 */
function summarize(
  value: unknown,
  allowedKeys: readonly string[],
  depth: number,
  seen: WeakSet<object>
): unknown {
  if (value === null || typeof value !== 'object') return redactValue(value, '', depth, seen)
  if (depth >= MAX_DEPTH) return '[Truncated]'
  if (seen.has(value)) return '[Circular]'
  seen.add(value)

  try {
    if (Array.isArray(value)) {
      return value.map((entry) => summarize(entry, allowedKeys, depth + 1, seen))
    }

    const out: UnknownRecord = {}
    for (const allowedKey of allowedKeys) {
      if (Object.hasOwn(value, allowedKey)) {
        out[allowedKey] = redactValue(
          (value as UnknownRecord)[allowedKey],
          allowedKey,
          depth + 1,
          seen
        )
      }
    }
    return out
  } finally {
    seen.delete(value)
  }
}

function redactValue(value: unknown, key: string, depth: number, seen: WeakSet<object>): unknown {
  const isContactKey = CONTACT_KEY_PATTERN.test(key) && !CONTACT_KEY_EXEMPTIONS.test(key)
  if (SECRET_KEY_PATTERN.test(key) || isContactKey) {
    return value === null || value === undefined ? value : FILTERED
  }

  if (typeof value === 'function') return '[Function]'
  if (value === null || typeof value !== 'object') return value
  if (value instanceof Date) return value

  if (depth >= MAX_DEPTH) return '[Truncated]'
  // Tracks the current path only, so an object referenced twice side by side
  // is redacted twice instead of being reported as a cycle.
  if (seen.has(value)) return '[Circular]'

  // Dispatched before this function starts tracking `value`, because summarize
  // does its own tracking and would otherwise see it as a cycle.
  // Own-property check: a prop or state key called `constructor`, `toString`
  // or similar would otherwise resolve to a function off Object.prototype.
  const allowedKeys = Object.hasOwn(PERSON_COLLECTION_KEYS, key)
    ? PERSON_COLLECTION_KEYS[key]
    : undefined
  if (allowedKeys) return summarize(value, allowedKeys, depth, seen)

  if (isRecord(value) && looksLikePerson(value)) {
    return summarize(value, PERSON_SHAPE_SAFE_KEYS, depth, seen)
  }

  seen.add(value)

  try {
    if (Array.isArray(value)) {
      return value.map((entry) => redactValue(entry, key, depth + 1, seen))
    }

    const out: UnknownRecord = {}
    for (const [childKey, childValue] of Object.entries(value)) {
      out[childKey] = redactValue(childValue, childKey, depth + 1, seen)
    }
    return out
  } finally {
    seen.delete(value)
  }
}

/** What follows the colon of a search line, up to the line's end in any encoding. */
const SEARCH_CODE = /(:|%(?:25)?3A)(?:(?!%(?:25)?0[AD])[^\n\r])*/gi

/** Cuts the transfer codes out of a domain search value, keeping each line's domain. */
export function redactSearchCodes(value: string): string {
  return value.replace(SEARCH_CODE, '$1')
}

/**
 * Redacts credentials out of a URL, for the event fields that hold one as a
 * plain string: `request.url`, the referrer, transaction names, span
 * descriptions and breadcrumb urls.
 *
 * Query parameters are matched by name against the same rule the object walk
 * uses, rather than against a hand-kept list of parameter names. That is what
 * makes it cover `payment_intent_client_secret` on the Stripe return pages
 * without anyone having remembered that flow: an attribute bag and a URL
 * disagreeing about what counts as a credential is how the last one got missed.
 *
 * The path rule is separate because the sub-account invite token is a path
 * segment, where there is no name to match on. So is the domain search's
 * `query` (and the palette's older `q`), which carried transfer codes as
 * "domain:code" lines and may still do in an old link: each line keeps its
 * domain.
 */
export function redactUrlCredentials(url: string): string {
  return url
    .replace(
      /([?&])([^=&#]+)=([^&#]*)/g,
      (match, separator: string, key: string, value: string) => {
        if (SECRET_KEY_PATTERN.test(key)) return `${separator}${key}=REDACTED`
        if (key === 'query' || key === 'q') return `${separator}${key}=${redactSearchCodes(value)}`
        return match
      }
    )
    .replace(/\/invite\/[^/?#]+/g, '/invite/REDACTED')
}

/**
 * Console breadcrumbs keep the logged arguments, objects included, so a URL
 * logged inside one is out of reach of the top-level breadcrumb fields. Plain
 * objects only: an Error copied into a new object would lose its message.
 */
export function redactConsoleBreadcrumb(breadcrumb: {
  message?: string
  data?: { [key: string]: unknown }
}): void {
  const redact = (value: unknown) =>
    typeof value === 'string' ? redactUrlCredentials(value) : value
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = redactUrlCredentials(breadcrumb.message)
  }
  const args = breadcrumb.data?.arguments
  if (!breadcrumb.data || !Array.isArray(args)) return
  breadcrumb.data.arguments = args.map((arg) =>
    isRecord(arg) && Object.getPrototypeOf(arg) === Object.prototype
      ? Object.fromEntries(Object.entries(arg).map(([key, value]) => [key, redact(value)]))
      : redact(arg)
  )
}

/**
 * Applies the same rules to any value Sentry is about to send, for the event
 * fields that are not Pinia state. Used for the props the Vue integration
 * attaches to `contexts.vue.propsData`, which can be a whole contact record.
 */
export function redactSentryValue(value: unknown): unknown {
  try {
    return redactValue(value, '', 0, new WeakSet<object>())
  } catch {
    return REDACTION_FAILED
  }
}

/**
 * Span attribute bags get two passes: by key name, which drops anything called
 * a credential while keeping `params.id`, and then over the values, since an
 * attribute under an innocent name can still be a token-bearing URL.
 */
export function redactSpanAttributes<T extends UnknownRecord>(data: T): T {
  // A copy, since a failed walk returns the shared REDACTION_FAILED object.
  const scrubbed = { ...(redactSentryValue(data) as UnknownRecord) }
  for (const [key, value] of Object.entries(scrubbed)) {
    if (typeof value === 'string') scrubbed[key] = redactUrlCredentials(value)
  }
  // SDK v11 records the query string without its `?`, which hides the first
  // parameter from the URL rule.
  for (const key of ['url.query', 'http.query']) {
    const value = scrubbed[key]
    if (typeof value === 'string' && !value.startsWith('?')) {
      scrubbed[key] = redactUrlCredentials(`?${value}`).slice(1)
    }
  }
  // Route query values arrive bare, which the URL rule cannot match.
  for (const key of ['query.q', 'query.query']) {
    const value = scrubbed[key]
    if (typeof value === 'string') {
      scrubbed[key] = redactSearchCodes(value)
    } else if (Array.isArray(value)) {
      scrubbed[key] = value.map((v) => (typeof v === 'string' ? redactSearchCodes(v) : v))
    }
  }
  return scrubbed as T
}

/**
 * `beforeSendSpan` for the static trace lifecycle. INP spans leave as
 * standalone spans outside any transaction, so this is the only hook that sees
 * them. Fails closed: the SDK sends the span unmodified if the callback throws.
 */
export function redactStaticSpan<T extends { description?: string; data?: UnknownRecord }>(
  span: T
): T {
  try {
    if (typeof span.description === 'string') {
      span.description = redactUrlCredentials(span.description)
    }
    if (span.data) span.data = redactSpanAttributes(span.data)
  } catch {
    span.description = 'REDACTED'
    span.data = { ...REDACTION_FAILED }
  }
  return span
}

/**
 * `stateTransformer` for Sentry's Pinia plugin. Receives every store's state
 * keyed by store id and must return a redacted copy: the input is the live
 * reactive state and must never be mutated.
 *
 * Fails closed, and says so rather than sending an empty object: the SDK
 * catches a throwing transformer and falls back to the raw states, which would
 * ship exactly what this module exists to withhold. One property getter that
 * throws anywhere in any store would be enough.
 */
export function redactPiniaState(states: UnknownRecord): UnknownRecord {
  try {
    const out: UnknownRecord = {}

    for (const [storeId, storeState] of Object.entries(states)) {
      // The store id is not passed as the key: it names a store, not a field,
      // and must not be matched against the key patterns.
      const redacted = redactValue(storeState, '', 0, new WeakSet<object>())

      // The session JWT lives under a key ("state") too generic to match by name.
      if (storeId === 'jwt' && isRecord(redacted) && redacted.state) {
        redacted.state = FILTERED
      }

      out[storeId] = redacted
    }

    return out
  } catch {
    return REDACTION_FAILED
  }
}
